import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  inject,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { shouldIgnorePaginatorDocumentShortcut } from '../directives/keystroke/paginator-keyboard-guards';

/** Must match removeEventListener third argument. */
const WINDOW_KEYDOWN_CAPTURE_NON_PASSIVE: AddEventListenerOptions = {
  capture: true,
  passive: false,
};

export interface PaginatorArrowRegistryEntry {
  host: HTMLElement;
  paginator: MatPaginator;
  renderer: Renderer2;
}

/**
 * Single `window` keydown capture listener registered at app startup (`APP_INITIALIZER`),
 * before Material attaches its own listeners — so Ctrl+Arrow pagination runs first and
 * rows-per-page mat-select does not see the key.
 */
@Injectable({ providedIn: 'root' })
export class PaginatorArrowWindowRegistryService {
  private readonly entries = new Map<HTMLElement, PaginatorArrowRegistryEntry>();
  private readonly onWindowKeydownCapture = (event: Event): void => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }
    const ev = event;
    if (ev.repeat) {
      return;
    }
    if (!ev.ctrlKey || ev.altKey || ev.metaKey || ev.shiftKey) {
      return;
    }
    let codeHandled: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | null = null;
    switch (ev.code) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown':
        codeHandled = ev.code;
        break;
      default:
        return;
    }

    for (const host of [...this.entries.keys()]) {
      if (!host.isConnected) {
        this.entries.delete(host);
        continue;
      }
      const entry = this.entries.get(host);
      if (!entry) {
        continue;
      }
      if (host.tagName.toLowerCase() !== 'mat-paginator') {
        continue;
      }
      if (shouldIgnorePaginatorDocumentShortcut(host)) {
        continue;
      }

      if (ev.cancelable) {
        ev.preventDefault();
      }
      ev.stopPropagation();
      ev.stopImmediatePropagation();

      const { paginator, renderer } = entry;
      switch (codeHandled) {
        case 'ArrowLeft':
          paginator.previousPage();
          this.focusPreviousIcon(renderer, host);
          return;
        case 'ArrowRight':
          paginator.nextPage();
          this.focusNextIcon(renderer, host);
          return;
        case 'ArrowUp':
          paginator.firstPage();
          return;
        case 'ArrowDown':
          paginator.lastPage();
          this.focusLastIcon(renderer, host);
          return;
        default:
          return;
      }
    }
  };

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) {
      return;
    }
    window.addEventListener(
      'keydown',
      this.onWindowKeydownCapture,
      WINDOW_KEYDOWN_CAPTURE_NON_PASSIVE
    );
  }

  register(
    host: HTMLElement,
    paginator: MatPaginator,
    renderer: Renderer2
  ): () => void {
    this.entries.set(host, { host, paginator, renderer });
    return () => void this.entries.delete(host);
  }

  private focusLastIcon(renderer: Renderer2, host: HTMLElement): void {
    const el = host.querySelector('.mat-paginator-navigation-last');
    if (el) {
      renderer.selectRootElement(el).focus();
    }
  }

  private focusNextIcon(renderer: Renderer2, host: HTMLElement): void {
    const el = host.querySelector('.mat-paginator-navigation-next');
    if (el) {
      renderer.selectRootElement(el).focus();
    }
  }

  private focusPreviousIcon(renderer: Renderer2, host: HTMLElement): void {
    const el = host.querySelector('.mat-paginator-navigation-previous');
    if (el) {
      renderer.selectRootElement(el).focus();
    }
  }
}
